import React from 'react'
import SearchUnitOfferDetailsModal from './SearchUnitOfferDetailsModal'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {showItemEditor} from '../../store/slices/shoppingCartSlice'
import {formatCurrency, generateUUID} from '../../services/utils'
import clsx from 'clsx'
import {selectSearchParams} from '../../store/slices/searchParamsSlice'
import {getLogger} from '../../services/logging'
import {pushDataLayerEvent} from '../../services/dataLayer'
import MemberRateBadge from '../common/MemberRateBadge'
import SpecialRateBadge from '../common/SpecialRateBadge'
import {booking} from '@whyhotel/common-schema'
import {calcNightlyBaseRate, calcTotalBaseRate} from '../../services/offerUtils'

const logger = getLogger('SearchUnitOfferItem')

export interface SearchUnitOfferItemProps {
  propertyId: string
  offer: booking.BookingOffer
}

const SearchUnitOfferItem = ({offer, propertyId}: SearchUnitOfferItemProps) => {
  const dispatch = useAppDispatch()
  const [showPricingDetails, setShowPricingDetails] = React.useState(false)
  const searchParams = useAppSelector(selectSearchParams)
  const [showOfferDetailsModal, setShowOfferDetailsModal] = React.useState(
    false
  )
  const nights = offer.timeSlices?.length
  const totalBaseRate = calcTotalBaseRate(offer)
  const nightlyBaseRate = calcNightlyBaseRate(offer)
  const promoCode = offer?.promoCode
  const corporateCode = offer?.corporateCode
  const isMemberRate = offer?.isMemberRate

  const handleShowDetailsModal = () => {
    pushDataLayerEvent({
      event: 'Unit_Rate_Details_View',
      category: 'IBE',
      value: 1
    })
    logger.info({
      message: 'User clicked rate details',
      data: {offer}
    })
    setShowOfferDetailsModal(true)
  }

  logger.debug({
    message: 'Rendering SearchUnitOfferItem...',
    data: {offer, nights, totalBaseRate}
  })

  return (
    <>
      <div className="w-full" data-rate-plan-id={offer?.ratePlan?.id}>
        {/* promo code badge */}
        {promoCode && <SpecialRateBadge label={promoCode} className="mb-2" />}

        {/* corporate code badge */}
        {corporateCode && (
          <SpecialRateBadge label={corporateCode} className="mb-2" />
        )}

        {/* member rate badge */}
        {isMemberRate && <MemberRateBadge className="mb-2" />}

        <div
          className={clsx(
            'flex flex-col gap-2 w-full',
            'lg:flex-row lg:items-center'
          )}
        >
          <div className="w-full leading-tight lg:w-3/5">
            <h1 className="text-md font-semibold">{offer.ratePlan.name}</h1>
            <a
              className={clsx(
                'link text-sm text-light-primary',
                'no-underline hover:underline'
              )}
              onClick={handleShowDetailsModal}
              onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                if (e.key === 'Enter') {
                  handleShowDetailsModal()
                }
              }}
              tabIndex={0}
              aria-label="View rate details for this room."
            >
              View rate details
            </a>
          </div>
          <div
            className={clsx(
              'w-full flex gap-2 flex-row items-center justify-between',
              'lg:w-2/5 lg:items-start lg:flex-row'
            )}
          >
            {/* offer pricing */}
            <div className="flex flex-col items-start lg:items-center justify-center">
              <div className="flex flex-row items-center font-semibold leading-none">
                <span className="text-lg mr-1">
                  {formatCurrency(nightlyBaseRate)}
                </span>
                <span className="text-xs uppercase">
                  {offer.totalGrossAmount.currency}
                </span>
              </div>

              <div
                className={clsx('dropdown', {
                  'dropdown-open': showPricingDetails
                })}
              >
                <div>
                  {/* pricing detail popover link */}
                  <a
                    className="link text-gray-500 text-xs leading-none text-center flex flex-row gap-1 items-center"
                    onClick={() => {
                      setShowPricingDetails(!showPricingDetails)
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        setShowPricingDetails(!showPricingDetails)
                      }
                    }}
                    onBlur={() => {
                      setShowPricingDetails(false)
                    }}
                    tabIndex={0}
                    aria-label="Show pricing details."
                  >
                    {formatCurrency(totalBaseRate)} total for{' '}
                    {nights === 1 ? '1 night' : `${nights} nights`}
                  </a>
                </div>
                {/* pricing detail popover */}
                <div
                  tabIndex={0}
                  className="card compact dropdown-content bg-white border rounded-box z-[1] w-64 shadow-lg"
                >
                  <div tabIndex={0} className="card-body">
                    <div className="flex flex-col gap-4">
                      <div>
                        {formatCurrency(nightlyBaseRate)} average nightly rate
                      </div>
                      <div className="divider leading-none mt-0 mb-0 h-1" />
                      <div>
                        {formatCurrency(totalBaseRate)} total for {nights}{' '}
                        nights
                        <br />+ taxes and fees
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* offer select button */}
            <button
              className="btn btn-sm btn-secondary self-center"
              onClick={() => {
                pushDataLayerEvent({
                  event: 'Unit_Select',
                  category: 'IBE',
                  value: 1
                })
                logger.info({
                  message: 'User selected unit offer',
                  data: {propertyId, offer}
                })
                // show item editor modal
                dispatch(
                  showItemEditor({
                    item: {
                      id: generateUUID(),
                      propertyId,
                      offer,
                      additionalServices: [],
                      guests: searchParams?.guests
                    },
                    mode: 'Add'
                  })
                )
              }}
              aria-label="Select the room offer and add to cart."
            >
              select
            </button>
          </div>
        </div>
      </div>

      <SearchUnitOfferDetailsModal
        offer={offer}
        isOpen={showOfferDetailsModal}
        onClose={() => setShowOfferDetailsModal(false)}
      />
    </>
  )
}

export default SearchUnitOfferItem
